import { setBrowserSessionId } from './Session';
import { encryptData, decryptData } from './Crypto';
import { store, authPersistConfig, cartPersistConfig } from '@store/online/index';
import { purgeStoredState } from 'redux-persist';
import { setRestaurantSetting } from '@store/online/restaurant/restaurantSlice';
import { resetCartSlice } from '@store/online/cart/cartSlice';
import { logoutUser, updateUserAuth } from '@store/online/auth/authSlice';
import { clearUserGiftCardData } from '@store/online/giftCards/giftCardSlice';
import { FlowType, resetFlow } from '@store/online/navigation/navigationSlice';

export const AUTH_SESSION_KEYS = [
  'AccessToken',
  'RefreshToken',
  'IdToken',
  'sub',
  'session',
  'username',
  'phone',
  'isSocialLogin',
  'validatedSocialData',
];

export const AUTH_COOKIE_KEYS = ['AccessToken', 'RefreshToken', 'IdToken', 'sub'];

const setToLocalStorage = (key: string, value: object | string | null) => {
  if (typeof window === 'undefined') return;
  if (!key || typeof key !== 'string') {
    throw new Error('Invalid key');
  }

  if (typeof value === 'object') {
    localStorage.setItem(key, JSON.stringify(value));
  } else {
    localStorage.setItem(key, value);
  }
};

const setToLocalStorageEnc = (key: string, value: object | string | null) => {
  if (!key || typeof key !== 'string') {
    throw new Error('Invalid key');
  }
  if (!value || value === null || value === '') {
    localStorage.removeItem(key);
    return;
  }
  const encryptedData = encryptData(value);
  localStorage.setItem(key, encryptedData);
};
const getLocalStorage = (key: string | object) => {
  if (!key || typeof key !== 'string') {
    throw new Error('Invalid key');
  }

  /**
   * Handle non-string value with JSON.parse
   * Catch string value and return it
   */
  try {
    const value = localStorage.getItem(key);
    if (typeof value === 'string') {
      return JSON.parse(value);
    }
  } catch {
    return localStorage.getItem(key);
  }
};

const getLocalStorageDec = (key: string | object) => {
  if (typeof window === 'undefined') return null;
  if (!key || typeof key !== 'string') {
    throw new Error('Invalid key');
  }

  /**
   * Handle non-string value with JSON.parse
   * Catch string value and return it
   */
  try {
    const value = localStorage.getItem(key);
    if (typeof value === 'string') {
      const decryptedData = decryptData(value);

      if (!decryptedData || decryptedData.length === 0) {
        return value;
      }
      return decryptedData;
    }
  } catch {
    return localStorage.getItem(key);
  }
};

const removeLocalStorage = (key: string | object) => {
  if (!key || typeof key !== 'string') {
    throw new Error('Invalid key');
  }

  localStorage.removeItem(key);
};

const clearReduxStorage = () => {
  const tenantId = store.getState().restaurant.selectedBusinessId;
  const tenantName = store.getState().restaurant.tenantName;
  const isQr = store.getState().restaurant.isQr;
  const qrId = store.getState().restaurant.qrId;
  localStorage.clear();
  AUTH_SESSION_KEYS.forEach((key) => sessionStorage.removeItem(key));
  sessionStorage.removeItem('tipsAltered');
  // Remove auth and cart from IndexedDB so stale login/cart state is not re-hydrated on next page load.
  // purgeStoredState targets individual slice keys, leaving restaurant/menu/etc intact.
  purgeStoredState(authPersistConfig);
  purgeStoredState(cartPersistConfig);
  store.dispatch(resetCartSlice());
  store.dispatch(logoutUser());
  store.dispatch(resetFlow({ flow: FlowType.PAYMENT }));
  store.dispatch(resetFlow({ flow: FlowType.GIFT_CARD }));
  store.dispatch(clearUserGiftCardData());
  store.dispatch(updateUserAuth({ signoutClicked: true }));
  if (tenantId) store.dispatch(setRestaurantSetting({ isQr, qrId, tenantName, selectedBusinessId: tenantId }));
  else store.dispatch(setRestaurantSetting({ isQr, qrId, tenantName }));
  setBrowserSessionId();
};

function setSessionStorage<T>(key: string, value: T): void {
  if (typeof window === 'undefined') return; // Make sure it's running in browser

  try {
    const serializedValue = JSON.stringify(value);
    window.sessionStorage.setItem(key, serializedValue);
  } catch (error) {
    console.error(`Error setting sessionStorage key "${key}":`, error);
  }
}

function setSessionStorageEnc<T>(key: string, value: T): void {
  if (typeof window === 'undefined') return; // Make sure it's running in browser
  if (!key || typeof key !== 'string') {
    throw new Error('Invalid key');
  }
  if (value === null || value === '') {
    window.sessionStorage.removeItem(key);
    return;
  }

  try {
    const serializedValue = JSON.stringify(value);
    const encryptedData = encryptData(serializedValue);
    window.sessionStorage.setItem(key, encryptedData);
  } catch (error) {
    console.error(`Error setting sessionStorage key "${key}":`, error);
  }
}

function getSessionStorageDec<T>(key: string): T | null {
  if (typeof window === 'undefined') return null; // Only run in browser

  try {
    const item = window.sessionStorage.getItem(key);
    if (!item) return null;

    const decrypted = decryptData(item);
    const data = decrypted && decrypted.length > 0 ? decrypted : item;

    return JSON.parse(data) as T;
  } catch (error) {
    console.error(`Error retrieving sessionStorage key "${key}":`, error);
    return null;
  }
}

function getSessionStorage<T>(key: string): T | null {
  if (typeof window === 'undefined') return null; // Make sure it's running in browser

  try {
    const item = window.sessionStorage.getItem(key);
    if (!item) return null;
    return JSON.parse(item) as T;
  } catch (error) {
    console.error(`Error parsing sessionStorage key "${key}":`, error);
    return null;
  }
}

function removeSessionStorage(key: string): void {
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.removeItem(key);
  } catch (error) {
    console.error(`Error removing sessionStorage key "${key}":`, error);
  }
}

export {
  setToLocalStorage,
  getLocalStorage,
  removeLocalStorage,
  clearReduxStorage,
  setToLocalStorageEnc,
  getLocalStorageDec,
  setSessionStorage,
  setSessionStorageEnc,
  getSessionStorage,
  getSessionStorageDec,
  removeSessionStorage,
};
